import React from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export const inputBorderStyles = {
    '.MuiOutlinedInput-notchedOutline': { borderColor: '#d0d5dd !important' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#39ACE7 !important' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#39ACE7 !important' },
};

export const labelStyle = {
    '&.MuiInputLabel-shrink': { color: '#6b7280 !important' },
};

interface SectionHeaderProps {
    title: string;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
        <Typography sx={{ color: '#009BD8', fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap', mr: 2 }}>
            {title}
        </Typography>
        <Box sx={{ flex: 1, height: '2px', backgroundColor: '#39ACE7' }} />
    </Box>
);

interface StyledTextFieldProps {
    label: string;
    required?: boolean;
    placeholder?: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    error?: boolean;
    helperText?: string;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    hideLabel?: boolean;
    disabled?: boolean;
    bgColor?: string;
    height?: number | string;
    borderRadius?: number | string;
    borderColor?: string;
    sx?: object;
}

export const StyledTextField: React.FC<StyledTextFieldProps> = ({
    label,
    required,
    placeholder,
    value,
    onChange,
    onBlur,
    error,
    helperText,
    inputProps,
    hideLabel,
    disabled,
    bgColor,
    height,
    borderRadius,
    borderColor,
    sx: customSx
}) => {
    const computedPlaceholder = placeholder || (required ? `${label}*` : label);

    return (
        <TextField
            size="small"
            fullWidth
            disabled={disabled}
            label={hideLabel ? undefined : label}
            required={hideLabel ? false : required}
            placeholder={hideLabel ? computedPlaceholder : placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={error}
            helperText={helperText}
            inputProps={inputProps}
            InputLabelProps={hideLabel ? undefined : { shrink: true, sx: labelStyle }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    backgroundColor: bgColor || '#fff',
                    height: height || 'auto',
                    borderRadius: borderRadius !== undefined ? borderRadius : undefined,
                    ...inputBorderStyles,
                    ...(borderColor && {
                        '.MuiOutlinedInput-notchedOutline': { borderColor: `${borderColor} !important` }
                    }),
                    // Disabled: darker background + readable text, clearly distinct from enabled
                    '&.Mui-disabled': {
                        backgroundColor: '#f3f4f6 !important',
                    },
                },
                '& .MuiInputBase-input': { color: '#000 !important' },
                // Disabled input text: target the actual <input> element only — cannot cascade to buttons
                '& input.Mui-disabled': {
                    WebkitTextFillColor: '#374151 !important',
                    opacity: 1,
                },
                '& .MuiFormHelperText-root': { ml: 0 },
                ...customSx
            }}
        />
    );
};

interface StyledSelectProps {
    label: string;
    value?: string;
    options?: { value: string; label: string }[];
    onChange?: (value: string) => void;
    required?: boolean;
    half?: boolean;
    hideLabel?: boolean;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    bgColor?: string;
    height?: number | string;
    borderRadius?: number | string;
    borderColor?: string;
    sx?: object;
}

export const ewsSelectMenuProps = {
    PaperProps: {
        sx: {
            backgroundColor: '#ffffff !important',
            color: '#000000 !important',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12) !important',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            mt: 0.5,
            maxHeight: 280,
            '& .MuiMenu-list': {
                backgroundColor: '#ffffff !important',
                padding: '4px 0',
            },
            '& .MuiList-root': {
                backgroundColor: '#ffffff !important',
            },
            '& .MuiMenuItem-root': {
                color: '#1e293b !important',
                WebkitTextFillColor: '#1e293b !important',
                backgroundColor: '#ffffff !important',
                fontSize: '0.85rem',
                minHeight: '36px',
                py: 0.75,
                px: 2,
                '&:hover': {
                    backgroundColor: '#f1f5f9 !important',
                },
                '&.Mui-selected': {
                    backgroundColor: '#e0f2fe !important',
                    color: '#0284c7 !important',
                    fontWeight: 600,
                    '&:hover': {
                        backgroundColor: '#bae6fd !important',
                    },
                },
                '&.Mui-disabled': {
                    color: '#94a3b8 !important',
                    WebkitTextFillColor: '#94a3b8 !important',
                    backgroundColor: '#ffffff !important',
                    opacity: 0.7,
                }
            },
        },
    },
};

export const ewsAutocompletePaperSx = {
    backgroundColor: '#ffffff !important',
    color: '#000000 !important',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12) !important',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    maxHeight: 280,
    '& .MuiAutocomplete-listbox': {
        backgroundColor: '#ffffff !important',
        padding: '4px 0',
    },
    '& .MuiAutocomplete-option': {
        color: '#1e293b !important',
        WebkitTextFillColor: '#1e293b !important',
        backgroundColor: '#ffffff !important',
        fontSize: '0.85rem',
        minHeight: '36px',
        py: 0.75,
        px: 2,
        '& .MuiTypography-root': {
            color: '#1e293b !important',
        },
        '&:hover, &.Mui-focused': {
            backgroundColor: '#f1f5f9 !important',
        },
        '&[aria-selected="true"], &.Mui-selected': {
            backgroundColor: '#e0f2fe !important',
            color: '#0284c7 !important',
            fontWeight: 600,
            '& .MuiTypography-root': {
                color: '#0284c7 !important',
            },
            '&:hover': {
                backgroundColor: '#bae6fd !important',
            },
        },
        '&.Mui-disabled': {
            color: '#94a3b8 !important',
            WebkitTextFillColor: '#94a3b8 !important',
            backgroundColor: '#ffffff !important',
            opacity: 0.7,
        }
    }
};

export const StyledSelect: React.FC<StyledSelectProps> = ({
    label,
    value = '',
    options = [],
    onChange,
    required,
    hideLabel,
    placeholder,
    error,
    helperText,
    disabled,
    bgColor,
    height,
    borderRadius,
    borderColor,
    sx: customSx
}) => {
    const computedPlaceholder = placeholder || (required ? `${label}*` : label);

    return (
        <FormControl size="small" fullWidth error={error} disabled={disabled} sx={customSx}>
            {!hideLabel && <InputLabel shrink sx={labelStyle}>{label}{required ? ' *' : ''}</InputLabel>}
            <Select
                value={value}
                displayEmpty
                notched={!hideLabel}
                label={hideLabel ? undefined : `${label}${required ? ' *' : ''}`}
                onChange={(e) => onChange?.(e.target.value as string)}
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={ewsSelectMenuProps}
                sx={{
                    '.MuiSelect-select': {
                        py: height ? 0 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        height: height ? `${typeof height === 'number' ? `${height}px` : height}` : 'auto',
                        color: value ? '#000 !important' : '#a0aab4 !important'
                    },
                    backgroundColor: bgColor || '#fff',
                    height: height || 'auto',
                    borderRadius: borderRadius !== undefined ? borderRadius : undefined,
                    ...inputBorderStyles,
                    '.MuiSelect-icon': { color: '#6b7280' },
                    ...(borderColor && {
                        '.MuiOutlinedInput-notchedOutline': { borderColor: `${borderColor} !important` }
                    }),
                    ...(error && {
                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important' },
                    }),
                    // Disabled: darker background + readable text, clearly distinct from enabled
                    '&.Mui-disabled': {
                        backgroundColor: '#f3f4f6 !important',
                    },
                    '&.Mui-disabled .MuiSelect-select': {
                        WebkitTextFillColor: '#374151 !important',
                        opacity: 1,
                    },
                    '&.Mui-disabled .MuiSelect-icon': {
                        color: '#6b7280 !important',
                        opacity: 0.6,
                    },
                }}
            >
                <MenuItem value="" disabled>{placeholder || (hideLabel ? computedPlaceholder : label)}</MenuItem>
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </Select>
            {helperText && (
                <Typography sx={{ fontSize: '0.75rem', color: error ? '#d32f2f' : '#6b7280', mt: 0.5, mx: 1.75 }}>
                    {helperText}
                </Typography>
            )}
        </FormControl>
    );
};

interface StyledTextAreaProps {
    label: string;
    rows?: number;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: boolean;
    disabled?: boolean;
    helperText?: string;
    hideLabel?: boolean;
    placeholder?: string;
    bgColor?: string;
    borderRadius?: number | string;
    borderColor?: string;
    sx?: object;
}

export const StyledTextArea: React.FC<StyledTextAreaProps> = ({
    label,
    rows = 3,
    value,
    onChange,
    error,
    disabled,
    helperText,
    hideLabel,
    placeholder,
    bgColor,
    borderRadius,
    borderColor,
    sx: customSx
}) => (
    <TextField
        size="small"
        fullWidth
        disabled={disabled}
        label={hideLabel ? undefined : label}
        placeholder={hideLabel ? (placeholder || label) : placeholder}
        multiline
        rows={rows}
        value={value}
        onChange={onChange}
        error={error}
        helperText={helperText}
        InputLabelProps={hideLabel ? undefined : { shrink: true, sx: labelStyle }}
        sx={{
            '& .MuiOutlinedInput-root': {
                backgroundColor: bgColor || '#fff',
                borderRadius: borderRadius !== undefined ? borderRadius : undefined,
                ...inputBorderStyles,
                ...(borderColor && {
                    '.MuiOutlinedInput-notchedOutline': { borderColor: `${borderColor} !important` }
                }),
                // Disabled: darker background, clearly distinct from enabled
                '&.Mui-disabled': {
                    backgroundColor: '#f3f4f6 !important',
                },
            },
            '& .MuiInputBase-input': { color: '#000 !important' },
            // Disabled textarea text: target the actual <textarea> element only — cannot cascade to buttons
            '& textarea.Mui-disabled': {
                WebkitTextFillColor: '#374151 !important',
                opacity: 1,
            },
            ...customSx
        }}
    />
);
